import React, { useCallback, useEffect, useState } from 'react';
import DashboardFooter from "../layouts/dashboard_footer";
import Canvas from "../layouts/dahboard_canvas";
import DashboardHeader from "../layouts/dashboard_header";
import SideMenu from "../layouts/dashboard_menu";
import { buildApiUrl, useBaseUrl } from "../../utils/utils";
import localForage from "localforage";
import Swal from "sweetalert2";

interface Ticket {
    id: number;
    subject: string;
    message: string;
    open: boolean;
    date_created: string;
    patient_id: string;
    patient_name: string;
    clinic_id: number;
}

interface ClinicPatientOption {
    patient_id: string;
    first_name: string;
    last_name: string;
    email: string;
}

const Tickets: React.FC = () => {
    const baseUrl = useBaseUrl();
    const [clinicId, setClinicId] = useState<number | null>(null);

    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [counts, setCounts] = useState({ open: 0, closed: 0 });
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    const [showCreate, setShowCreate] = useState(false);
    const [patientOptions, setPatientOptions] = useState<ClinicPatientOption[]>([]);
    const [patientSearch, setPatientSearch] = useState('');
    const [patientsLoading, setPatientsLoading] = useState(false);
    const [form, setForm] = useState({ patient_id: '', subject: '', message: '' });
    const [submitting, setSubmitting] = useState(false);

    const [viewingTicket, setViewingTicket] = useState<Ticket | null>(null);

    useEffect(() => {
        document.title = "Uzima Tickets";
        localForage.getItem("clinic_id").then((cid: any) => setClinicId(cid));
    }, []);

    const fetchTickets = useCallback(async () => {
        if (!clinicId) return;
        setLoading(true);
        try {
            const params = new URLSearchParams({
                clinic_id: String(clinicId),
                page: String(page),
                page_size: '15',
                search,
                status: statusFilter,
            });
            const res = await fetch(buildApiUrl(baseUrl, `facilities/tickets/?${params}`));
            const data = await res.json();
            if (data.status === 'success') {
                setTickets(data.data || []);
                setTotalPages(data.pagination?.total_pages || 1);
                setCounts(data.counts || { open: 0, closed: 0 });
            }
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Could not load tickets.' });
        } finally {
            setLoading(false);
        }
    }, [clinicId, baseUrl, page, search, statusFilter]);

    useEffect(() => { fetchTickets(); }, [fetchTickets]);

    useEffect(() => {
        if (!clinicId || !showCreate) return;
        (async () => {
            setPatientsLoading(true);
            try {
                const res = await fetch(buildApiUrl(baseUrl, `facilities/patients/list?clinic_id=${clinicId}&search=${encodeURIComponent(patientSearch)}&page_size=25`));
                const data = await res.json();
                setPatientOptions(data.data || []);
            } catch {
                setPatientOptions([]);
            } finally {
                setPatientsLoading(false);
            }
        })();
    }, [clinicId, baseUrl, showCreate, patientSearch]);

    const resetForm = () => setForm({ patient_id: '', subject: '', message: '' });

    const submitTicket = async () => {
        if (!form.patient_id || !form.subject.trim() || !form.message.trim()) {
            Swal.fire({ icon: 'warning', title: 'Missing Fields', text: 'Select a patient and fill in subject and message.' });
            return;
        }
        setSubmitting(true);
        try {
            const res = await fetch(buildApiUrl(baseUrl, `facilities/tickets/`), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ clinic_id: clinicId, ...form }),
            });
            const data = await res.json();
            if (res.ok && data.status === 'success') {
                Swal.fire({ icon: 'success', title: 'Ticket Created', timer: 1500, showConfirmButton: false });
                setShowCreate(false);
                resetForm();
                setPage(1);
                fetchTickets();
            } else {
                Swal.fire({ icon: 'error', title: 'Error', text: data.message || 'Failed to create ticket.' });
            }
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Network error while creating ticket.' });
        } finally {
            setSubmitting(false);
        }
    };

    const toggleTicketStatus = async (ticket: Ticket) => {
        const nextOpen = !ticket.open;
        const confirm = await Swal.fire({
            title: nextOpen ? 'Reopen Ticket?' : 'Close Ticket?',
            icon: 'question', showCancelButton: true, confirmButtonColor: '#0f766e',
            confirmButtonText: nextOpen ? 'Yes, reopen' : 'Yes, close',
        });
        if (!confirm.isConfirmed) return;
        try {
            const res = await fetch(buildApiUrl(baseUrl, `facilities/tickets/${ticket.id}/status`), {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ open: nextOpen }),
            });
            if (res.ok) {
                setViewingTicket(null);
                fetchTickets();
            }
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to update ticket.' });
        }
    };

    return (
        <>
            <DashboardHeader/>
            <SideMenu/>
            <div className={"vertical-overlay"}/>
            <div className={"main-content"}>
                <div className={"page-content"}>
                    <div className="container-fluid">

                        <div className={"row"}>
                            <div className={"col-12"}>
                                <div className={"page-title-box d-sm-flex align-items-center justify-content-between"}>
                                    <h4 className={"mb-sm-0"}>Uzima Tickets</h4>
                                    <div className={"page-title-right"}>
                                        <ol className={"breadcrumb m-0"}>
                                            <li className={"breadcrumb-item"}>Support</li>
                                            <li className={"breadcrumb-item active"}>Tickets</li>
                                        </ol>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={"row"}>
                            <div className={"col-xxl-3 col-sm-6"}>
                                <div className={"card card-animate"}>
                                    <div className={"card-body"}>
                                        <div className={"d-flex justify-content-between"}>
                                            <div>
                                                <p className={"fw-medium text-muted mb-0"}>Open Tickets</p>
                                                <h2 className={"mt-4 ff-secondary"}>{counts.open}</h2>
                                            </div>
                                            <div className="avatar-sm flex-shrink-0">
                                                <span className="avatar-title bg-soft-warning text-warning rounded-circle fs-4">
                                                    <i className="mdi mdi-timer-sand"></i>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-xxl-3 col-sm-6">
                                <div className="card card-animate">
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between">
                                            <div>
                                                <p className="fw-medium text-muted mb-0">Closed Tickets</p>
                                                <h2 className="mt-4 ff-secondary">{counts.closed}</h2>
                                            </div>
                                            <div className="avatar-sm flex-shrink-0">
                                                <span className="avatar-title bg-soft-success text-success rounded-circle fs-4">
                                                    <i className="ri-checkbox-circle-line"></i>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-xxl-3 col-sm-6">
                                <div className="card card-animate">
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between">
                                            <div>
                                                <p className="fw-medium text-muted mb-0">Total Tickets</p>
                                                <h2 className="mt-4 ff-secondary">{counts.open + counts.closed}</h2>
                                            </div>
                                            <div className="avatar-sm flex-shrink-0">
                                                <span className="avatar-title bg-soft-info text-info rounded-circle fs-4">
                                                    <i className="ri-ticket-2-line"></i>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-lg-12">
                                <div className="card" id="ticketsList">
                                    <div className="card-header border-0">
                                        <div className="d-flex align-items-center">
                                            <h5 className="card-title mb-0 flex-grow-1">Tickets</h5>
                                            <div className="flex-shrink-0">
                                                <button className="btn btn-danger add-btn" onClick={() => setShowCreate(true)}
                                                        style={{ background: '#0f766e', borderColor: '#0f766e' }}>
                                                    <i className="ri-add-line align-bottom me-1"> </i> Create Ticket
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="card-body border border-dashed border-end-0 border-start-0">
                                        <div className="row g-3">
                                            <div className="col-xxl-6 col-sm-8">
                                                <div className="search-box">
                                                    <input type="text" className="form-control search bg-light border-light"
                                                           placeholder="Search subject, message, or patient name..."
                                                           value={search}
                                                           onChange={(e) => { setSearch(e.target.value); setPage(1); }}/>
                                                    <i className="ri-search-line search-icon"></i>
                                                </div>
                                            </div>
                                            <div className="col-xxl-3 col-sm-4">
                                                <select className="form-control" value={statusFilter}
                                                        onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
                                                    <option value="">All Statuses</option>
                                                    <option value="open">Open</option>
                                                    <option value="closed">Closed</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="card-body">
                                        {loading ? (
                                            <div className="text-center py-5">
                                                <div className="spinner-border text-primary" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                            </div>
                                        ) : tickets.length === 0 ? (
                                            <div className="text-center py-5">
                                                <i className="ri-ticket-2-line" style={{ fontSize: '2.5rem' }}></i>
                                                <h5 className="mt-2">No tickets found</h5>
                                                <p className="text-muted mb-0">
                                                    {search || statusFilter ? 'No tickets match your filters.' : 'No support tickets have been raised for this clinic yet.'}
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="table-responsive table-card mb-4">
                                                <table className="table align-middle table-nowrap mb-0">
                                                    <thead>
                                                    <tr>
                                                        <th>ID</th>
                                                        <th>Subject</th>
                                                        <th>Patient</th>
                                                        <th>Created</th>
                                                        <th>Status</th>
                                                        <th>Action</th>
                                                    </tr>
                                                    </thead>
                                                    <tbody>
                                                    {tickets.map(ticket => (
                                                        <tr key={ticket.id}>
                                                            <td>
                                                                <a href="javascript:void(0);" className="fw-medium link-primary"
                                                                   onClick={() => setViewingTicket(ticket)}>#UZM-{String(ticket.id).padStart(3, '0')}</a>
                                                            </td>
                                                            <td>{ticket.subject}</td>
                                                            <td>{ticket.patient_name}</td>
                                                            <td>{new Date(ticket.date_created).toLocaleDateString()}</td>
                                                            <td>
                                                                <span className={`badge text-uppercase ${ticket.open ? 'badge-soft-warning' : 'badge-soft-success'}`}>
                                                                    {ticket.open ? 'Open' : 'Closed'}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <button className="btn btn-soft-secondary btn-sm" onClick={() => setViewingTicket(ticket)}>
                                                                    <i className="ri-eye-fill align-bottom"></i> View
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}

                                        {totalPages > 1 && (
                                            <div className="d-flex justify-content-end mt-2">
                                                <div className="pagination-wrap hstack gap-2">
                                                    <button className="page-item pagination-prev btn btn-link" disabled={page <= 1}
                                                            onClick={() => setPage(p => Math.max(1, p - 1))}>Previous</button>
                                                    <span className="align-self-center">Page {page} of {totalPages}</span>
                                                    <button className="page-item pagination-next btn btn-link" disabled={page >= totalPages}
                                                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next</button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Canvas/>
                <DashboardFooter/>
            </div>

            {showCreate && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Create Ticket</h5>
                                <button type="button" className="btn-close" onClick={() => { setShowCreate(false); resetForm(); }}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Patient</label>
                                    <input type="text" className="form-control mb-2" placeholder="Search patients..."
                                           value={patientSearch} onChange={(e) => setPatientSearch(e.target.value)}/>
                                    <select className="form-select" value={form.patient_id}
                                            onChange={(e) => setForm(f => ({ ...f, patient_id: e.target.value }))}>
                                        <option value="">{patientsLoading ? 'Loading patients...' : 'Select a patient'}</option>
                                        {patientOptions.map(p => (
                                            <option key={p.patient_id} value={p.patient_id}>
                                                {p.first_name} {p.last_name} ({p.email})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Subject</label>
                                    <input type="text" className="form-control" value={form.subject}
                                           onChange={(e) => setForm(f => ({ ...f, subject: e.target.value }))}
                                           placeholder="Brief summary of the issue"/>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Message</label>
                                    <textarea className="form-control" rows={4} value={form.message}
                                              onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))}
                                              placeholder="Describe the issue in detail"/>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => { setShowCreate(false); resetForm(); }}>Cancel</button>
                                <button className="btn btn-danger" style={{ background: '#0f766e', borderColor: '#0f766e' }}
                                        disabled={submitting} onClick={submitTicket}>
                                    {submitting ? 'Creating...' : 'Create Ticket'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {viewingTicket && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">#UZM-{String(viewingTicket.id).padStart(3, '0')} — {viewingTicket.subject}</h5>
                                <button type="button" className="btn-close" onClick={() => setViewingTicket(null)}></button>
                            </div>
                            <div className="modal-body">
                                <p><strong>Patient:</strong> {viewingTicket.patient_name}</p>
                                <p><strong>Created:</strong> {new Date(viewingTicket.date_created).toLocaleString()}</p>
                                <p><strong>Status:</strong> <span className={`badge text-uppercase ${viewingTicket.open ? 'badge-soft-warning' : 'badge-soft-success'}`}>{viewingTicket.open ? 'Open' : 'Closed'}</span></p>
                                <hr/>
                                <p style={{ whiteSpace: 'pre-wrap' }}>{viewingTicket.message}</p>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setViewingTicket(null)}>Close</button>
                                <button className="btn btn-danger" style={{ background: '#0f766e', borderColor: '#0f766e' }}
                                        onClick={() => toggleTicketStatus(viewingTicket)}>
                                    {viewingTicket.open ? 'Mark as Closed' : 'Reopen Ticket'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Tickets;
